/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { setTitle } from "@lemmonhq/orders";
import { ThemeSwitcher, useLocalization, SwissMade } from "@lemmonhq/uikit";
import { useGroupAuth } from "@app/vue/providers/GroupAuth";
import { useToast } from "@app/vue/providers/Toast";
import { getSupportedLanguagesForJurisdictions } from "@app/utils/supportedLanguages";
import { release, lemmonEnv } from "../../../../version";
import { MenuSection } from "../../Menu/MenuSection";
import { MenuListItem } from "../../Menu/MenuListItem";
import { RestaurantSwitcherImage } from "@app/vue/components/LayoutComponents/Navbar/RestaurantSwitcherImage";

export const GroupMenuPage = defineComponent({
  name: "GroupMenuPage",
  setup() {
    onMounted(() => setTitle("Menu"));

    const auth = useGroupAuth();
    const toast = useToast();
    const { t, locale, setLocale } = useLocalization();
    const loadingLanguage = ref<string | null>(null);
    const openingUserId = ref<number | null>(null);

    const openShop = async (userId: number) => {
      if (openingUserId.value !== null) return;
      openingUserId.value = userId;
      try {
        await auth.exitToShop(userId);
      } catch {
        openingUserId.value = null;
        toast.showToast(t("admin.shops.open_error"), { type: "error" });
      }
    };

    const setLanguage = async (language: string) => {
      loadingLanguage.value = language;
      await setLocale(language);
      loadingLanguage.value = null;
    };

    const appVersion = lemmonEnv?.app;

    return () => {
      const isStaging = import.meta.env.VITE_ENV === "staging";
      const availableShops = auth.shops.value.filter(
        (shop) =>
          shop.can_open_pos && shop.user_id && shop.pos_role === "manager",
      );
      const availableLanguages = getSupportedLanguagesForJurisdictions(
        auth.shops.value.map((shop) => shop.jurisdiction),
      );

      return (
        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto bg-base-100">
          <div
            style={{ paddingTop: "calc(0.75rem + var(--safe-area-top, 0px))" }}
            class="sticky top-0 z-10 flex items-center gap-3 border-b border-base-content/[0.06] bg-base-100/95 px-4 pb-3 backdrop-blur-md"
          >
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-base-content/10 bg-base-content/10 text-[11px] font-semibold text-base-content/70">
              {auth.identity.value.name
                .split(" ")
                .slice(0, 2)
                .map((part) => part[0])
                .join("")
                .toUpperCase()}
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-[13.5px] font-semibold leading-tight text-base-content/90">
                {auth.identity.value.name}
              </div>
              <div class="truncate text-[11px] leading-tight text-base-content/45">
                {auth.group.value.name}
              </div>
            </div>
          </div>

          {availableShops.length > 0 ? (
            <MenuSection title="admin.nav.return_to_pos">
              {availableShops.map((shop) => (
                <button
                  key={shop.id}
                  type="button"
                  disabled={openingUserId.value !== null}
                  class="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-base-content/5"
                  onClick={() => void openShop(shop.user_id!)}
                >
                  {openingUserId.value === shop.user_id ? (
                    <span class="loading loading-spinner loading-xs shrink-0" />
                  ) : (
                    <RestaurantSwitcherImage
                      name={shop.name}
                      url={shop.image_url}
                    />
                  )}
                  <span class="min-w-0 flex-1 truncate text-[14.5px] font-medium text-base-content/80">
                    {shop.name}
                  </span>
                  <span class="material-symbols-outlined shrink-0 text-[18px] text-base-content/25">
                    chevron_right
                  </span>
                </button>
              ))}
            </MenuSection>
          ) : null}

          <MenuSection title="menu.section.general">
            <MenuListItem
              to="/group/dashboard"
              icon="dashboard"
              title="admin.common.dashboard"
            />
            <MenuListItem
              to="/group/loyalty"
              icon="loyalty"
              title="nav.labels.loyalty"
            />
            {auth.isGroupAdmin.value ? (
              <MenuListItem
                to="/group/settings"
                icon="settings"
                title="admin.common.settings"
              />
            ) : null}
          </MenuSection>

          <MenuSection title="admin.nav.language">
            {availableLanguages.map((language) => (
              <button
                key={language.id}
                type="button"
                class="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-base-content/5"
                onClick={() => void setLanguage(language.id)}
              >
                {loadingLanguage.value === language.id ? (
                  <span class="loading loading-spinner loading-xs shrink-0" />
                ) : (
                  <span class="material-symbols-outlined shrink-0 text-[19px] text-base-content/50">
                    {locale.value === language.id
                      ? "radio_button_checked"
                      : "radio_button_unchecked"}
                  </span>
                )}
                <span class="min-w-0 flex-1 truncate text-[14.5px] font-medium text-base-content/80">
                  {language.name}
                </span>
              </button>
            ))}
          </MenuSection>

          {isStaging ? (
            <div class="px-4 pt-5">
              <div class="px-1 pb-2 text-[11px] font-semibold uppercase tracking-widest text-accent">
                {t("admin.nav.theme")}
              </div>
              <div class="overflow-hidden rounded-2xl border border-base-content/[0.06] bg-base-200/60 px-2 py-1">
                <ThemeSwitcher />
              </div>
            </div>
          ) : null}

          <div class="px-4 pt-5">
            <button
              type="button"
              onClick={() => void auth.logout()}
              class="flex w-full items-center justify-center gap-2 rounded-2xl border border-error/15 bg-error/5 px-4 py-3.5 text-[14.5px] font-semibold text-error/80 active:bg-error/10"
            >
              <span class="material-symbols-outlined text-[18px]">logout</span>
              {t("admin.common.logout")}
            </button>
          </div>

          <div class="flex flex-col items-center gap-2 px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-6">
            <SwissMade width={40} />
            <div class="text-center text-[10px] leading-tight text-base-content/30">
              {release}
              {appVersion?.version ? ` · v${appVersion.version}` : ""}
              {import.meta.env.VITE_ENV
                ? ` · ${(import.meta.env.VITE_ENV as string).toUpperCase()}`
                : ""}
            </div>
          </div>
        </div>
      );
    };
  },
});
